// frontend/src/components/postproduction/GenreBreakdown.tsx
import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Chip
} from '@mui/material';
import { PerformanceIntelligence } from '../../types/PPDash';

interface GenreBreakdownProps {
  performances: PerformanceIntelligence[];
}

interface GenreStats {
  genre: string;
  showCount: number;
  totalAudience: number;
  avgCapacity: number;
  avgSubsidy: number;
  totalRevenue: number;
  totalCosts: number;
  avgNoShowRate: number;
}

type SortField = 'genre' | 'showCount' | 'avgCapacity' | 'avgSubsidy' | 'totalAudience';
type SortOrder = 'asc' | 'desc';

const GenreBreakdown: React.FC<GenreBreakdownProps> = ({ performances }) => {
  const [sortField, setSortField] = React.useState<SortField>('showCount');
  const [sortOrder, setSortOrder] = React.useState<SortOrder>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder(field === 'genre' ? 'asc' : 'desc');
    }
  };

  // Calculate genre statistics
  const genreStats = useMemo(() => {
    const statsByGenre: { [key: string]: GenreStats } = {};

    performances.forEach(perf => {
      const genre = perf.genre || 'Unspecified';

      if (!statsByGenre[genre]) {
        statsByGenre[genre] = {
          genre,
          showCount: 0,
          totalAudience: 0,
          avgCapacity: 0,
          avgSubsidy: 0,
          totalRevenue: 0,
          totalCosts: 0,
          avgNoShowRate: 0
        };
      }

      const stats = statsByGenre[genre];
      stats.showCount += 1;
      stats.totalAudience += perf.attendance.totalAttendance;
      stats.totalRevenue += perf.financial.revenue;
      stats.totalCosts += perf.financial.costs;
    });

    // Calculate averages
    Object.keys(statsByGenre).forEach(genre => {
      const stats = statsByGenre[genre];
      
      // Get performances for this genre to calculate averages
      const genrePerformances = performances.filter(p => (p.genre || 'Unspecified') === genre);
      
      // Average capacity (only count performances with capacity data)
      const perfWithCapacity = genrePerformances.filter(p => p.attendance.capacity !== null);
      stats.avgCapacity = perfWithCapacity.length > 0
        ? perfWithCapacity.reduce((sum, p) => sum + (p.attendance.capacity || 0), 0) / perfWithCapacity.length
        : 0;

      // Average subsidy
      stats.avgSubsidy = genrePerformances.reduce((sum, p) => sum + p.financial.subsidy, 0) / stats.showCount;

      // Average no-show rate
      const perfWithNoShows = genrePerformances.filter(p => p.attendance.noShowRate > 0);
      stats.avgNoShowRate = perfWithNoShows.length > 0
        ? perfWithNoShows.reduce((sum, p) => sum + p.attendance.noShowRate, 0) / perfWithNoShows.length
        : 0;
    });

    return Object.values(statsByGenre);
  }, [performances]);

  // Sort genre stats
  const sortedStats = useMemo(() => {
    return [...genreStats].sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortField) {
        case 'genre':
          aVal = a.genre.toLowerCase();
          bVal = b.genre.toLowerCase();
          break;
        case 'showCount':
          aVal = a.showCount;
          bVal = b.showCount;
          break;
        case 'avgCapacity':
          aVal = a.avgCapacity;
          bVal = b.avgCapacity;
          break;
        case 'avgSubsidy':
          aVal = a.avgSubsidy;
          bVal = b.avgSubsidy;
          break;
        case 'totalAudience':
          aVal = a.totalAudience;
          bVal = b.totalAudience;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [genreStats, sortField, sortOrder]);

  const formatCurrency = (amount: number) => {
    return `€${amount.toLocaleString('en-IE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const getSubsidyColor = (subsidy: number) => {
    if (subsidy < 0) return 'success';
    if (subsidy < 100) return 'warning';
    return 'error';
  };

  if (genreStats.length === 0) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Genre Breakdown
        </Typography>
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: 'background.paper' }}>
          <Typography color="text.secondary">
            No genre data available for the selected performances
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Genre Breakdown
      </Typography>

      <TableContainer component={Paper} sx={{ backgroundColor: 'background.paper' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'genre'}
                  direction={sortField === 'genre' ? sortOrder : 'asc'}
                  onClick={() => handleSort('genre')}
                >
                  Genre
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'showCount'}
                  direction={sortField === 'showCount' ? sortOrder : 'desc'}
                  onClick={() => handleSort('showCount')}
                >
                  Shows
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'totalAudience'}
                  direction={sortField === 'totalAudience' ? sortOrder : 'desc'}
                  onClick={() => handleSort('totalAudience')}
                >
                  Total Audience
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'avgCapacity'}
                  direction={sortField === 'avgCapacity' ? sortOrder : 'desc'}
                  onClick={() => handleSort('avgCapacity')}
                >
                  Avg Capacity
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Avg Revenue</TableCell>
              <TableCell align="right">Avg Costs</TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'avgSubsidy'}
                  direction={sortField === 'avgSubsidy' ? sortOrder : 'desc'}
                  onClick={() => handleSort('avgSubsidy')}
                >
                  Avg Subsidy
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Avg No-Show</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedStats.map((stats) => (
              <TableRow 
                key={stats.genre}
                hover
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {stats.genre}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Chip 
                    label={stats.showCount} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  {stats.totalAudience.toLocaleString()}
                </TableCell>
                <TableCell align="right">
                  {stats.avgCapacity > 0 ? `${stats.avgCapacity.toFixed(1)}%` : 'N/A'}
                </TableCell>
                <TableCell align="right">
                  {formatCurrency(stats.totalRevenue / stats.showCount)}
                </TableCell>
                <TableCell align="right">
                  {formatCurrency(stats.totalCosts / stats.showCount)}
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={formatCurrency(stats.avgSubsidy)}
                    color={getSubsidyColor(stats.avgSubsidy)}
                    size="small"
                    sx={{ minWidth: 80 }}
                  />
                </TableCell>
                <TableCell align="right">
                  {stats.avgNoShowRate > 0 ? `${stats.avgNoShowRate.toFixed(1)}%` : 'N/A'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, textAlign: 'right' }}>
        <Typography variant="caption" color="text.secondary">
          Showing {sortedStats.length} genre{sortedStats.length !== 1 ? 's' : ''} • 
          Total {performances.length} performance{performances.length !== 1 ? 's' : ''}
        </Typography>
      </Box>
    </Box>
  );
};

export default GenreBreakdown;